import { createSlice, type PayloadAction } from "@reduxjs/toolkit";
import {
  IGuernseyType,
  TGuernseyData,
  TGuernseyRows,
} from "src/components/shared/GuernseyMulti/Guernsey.types";
import {
  type IAutofillPopup,
  type IBodyPartModalData,
  type IBodyPartPreviewModalData,
  type ICaseReportModalData,
  IDeisolatedTimeModalData,
  type IlModalData,
  type INMEAAmendModalData,
  INMEAAutomationModalData,
  type ITimeInputPopup,
  type UnitConverterModalData,
  type URNHistoryModalData,
} from "src/interfaces/Modals";
import { IPostRequest } from "src/interfaces/Post";

interface EngineSelectData {
  [key: string]: string; // engineColIndex -> selectedValue (e.g., "engine0" -> "PORT")
}

interface GeneratorFeaturesSlice {
  locked24SectionIndex: number;
  wasSaved: boolean;
  wasNMEAModalClosed: boolean;

  triggerTimeCheck: number;
  timeCheckIssueKeys: string[];
  popups: Record<string, boolean>;

  ilModalData: IlModalData | null;
  unitConverterModalData: UnitConverterModalData | null;
  URNHistoryModalData: URNHistoryModalData | null;
  timeInputPopup: ITimeInputPopup | null;
  autofillPopup: IAutofillPopup | null;
  bodyPartModalData: IBodyPartModalData | null;
  bodyPartPreviewModalData: IBodyPartPreviewModalData | null;
  caseReportModalData: ICaseReportModalData | null;
  deisolatedTimeModalData: IDeisolatedTimeModalData | null;
  guernseyType: IGuernseyType | null;
  guernseyData: TGuernseyData | null;
  NMEAModalData: INMEAAutomationModalData | null;
  NMEAAmendModalData: INMEAAmendModalData | null;
  newEntryRequests: IPostRequest[];
  engineSelectData: EngineSelectData;
}

const initialState: GeneratorFeaturesSlice = {
  locked24SectionIndex: -1,
  newEntryRequests: [],

  triggerTimeCheck: -1,
  timeCheckIssueKeys: [],
  popups: {},
  wasSaved: false,
  wasNMEAModalClosed: false,

  ilModalData: null,
  unitConverterModalData: null,
  URNHistoryModalData: null,
  timeInputPopup: null,
  autofillPopup: null,
  bodyPartModalData: null,
  bodyPartPreviewModalData: null,
  caseReportModalData: null,
  deisolatedTimeModalData: null,

  guernseyType: null,
  guernseyData: null,
  NMEAModalData: null,
  NMEAAmendModalData: null,
  engineSelectData: {},
};

export const generatorFeaturesSlice = createSlice({
  name: "generatorFeaturesSlice",
  initialState,
  reducers: {
    setLocked24SectionIndex: (state, action: PayloadAction<number>) => {
      state.locked24SectionIndex = action.payload;
    },
    setTriggerTimeCheck: (state, action: PayloadAction<number>) => {
      state.triggerTimeCheck = action.payload;
    },
    setTimeCheckIssueKeys: (state, action: PayloadAction<string[]>) => {
      state.timeCheckIssueKeys = action.payload;
    },
    setBodyPartPreviewModalData: (
      state,
      action: PayloadAction<IBodyPartPreviewModalData | null>,
    ) => {
      state.bodyPartPreviewModalData = action.payload;
    },

    setBodyPartModalData: (
      state,
      action: PayloadAction<IBodyPartModalData | null>,
    ) => {
      state.bodyPartModalData = action.payload;
    },
    setIlModalData: (state, action: PayloadAction<IlModalData | null>) => {
      state.ilModalData = action.payload;
    },
    setGuernseyType: (state, action: PayloadAction<IGuernseyType | null>) => {
      state.guernseyType = action.payload;
    },
    setGuernseyData: (state, action: PayloadAction<TGuernseyData | null>) => {
      state.guernseyData = action.payload;
    },
    addGuernseyData: (
      state,
      action: PayloadAction<{ rowIndex: number; rows: TGuernseyRows }>,
    ) => {
      const { rowIndex, rows } = action.payload;
      if (!state.guernseyData) {
        state.guernseyData = {};
      }

      state.guernseyData[rowIndex] = rows;
    },
    removeGuernseyRow: (state, action: PayloadAction<{ rowIndex: number }>) => {
      const { rowIndex } = action.payload;
      if (state.guernseyData?.[rowIndex]) {
        delete state.guernseyData?.[rowIndex];
      }
    },
    setDeisolatedTimeModalOpen: (
      state,
      action: PayloadAction<IDeisolatedTimeModalData | null>,
    ) => {
      state.deisolatedTimeModalData = action.payload;
    },
    setUnitConverterModalData: (
      state,
      action: PayloadAction<UnitConverterModalData | null>,
    ) => {
      state.unitConverterModalData = action.payload;
    },

    setURNHistoryModalData: (
      state,
      action: PayloadAction<URNHistoryModalData | null>,
    ) => {
      state.URNHistoryModalData = action.payload;
    },
    setCaseReportModalData: (
      state,
      action: PayloadAction<ICaseReportModalData | null>,
    ) => {
      state.caseReportModalData = action.payload;
    },
    setTimeInputPopup: (
      state,
      action: PayloadAction<ITimeInputPopup | null>,
    ) => {
      state.timeInputPopup = action.payload;
    },
    setAutofillPopup: (state, action: PayloadAction<IAutofillPopup | null>) => {
      state.autofillPopup = action.payload;
    },
    setPopup: (
      state,
      action: PayloadAction<{ inputId: string; value: boolean }>,
    ) => {
      state.popups[action.payload.inputId] = action.payload.value;
    },
    clearPopups: state => {
      state.popups = {};
    },
    // setWasSaved: (state, action: PayloadAction<boolean>) => {
    //   state.wasSaved = action.payload;
    // },
    // setWasNMEAModalClosed: (state, action: PayloadAction<boolean>) => {
    //   state.wasNMEAModalClosed = action.payload;
    // },
    setNMEAModalData: (
      state,
      action: PayloadAction<INMEAAutomationModalData | null>,
    ) => {
      state.NMEAModalData = action.payload;
    },
    setNMEAAmendModalData: (
      state,
      action: PayloadAction<INMEAAmendModalData | null>,
    ) => {
      state.NMEAAmendModalData = action.payload;
    },
    clearNMEAAmendModalData: state => {
      state.NMEAAmendModalData = null;
    },
    addNewEntryRequest: (state, action: PayloadAction<IPostRequest>) => {
      state.newEntryRequests = [...state.newEntryRequests, action.payload];
    },
    setNewEntryRequest: (state, action: PayloadAction<IPostRequest[]>) => {
      state.newEntryRequests = action.payload;
    },
    setEngineSelectData: (
      state,
      action: PayloadAction<{ colIndex: number; value: string }>,
    ) => {
      const { colIndex, value } = action.payload;
      const key = `engine${colIndex}`;
      state.engineSelectData[key] = value;
    },
    clearEngineSelectData: state => {
      state.engineSelectData = {};
    },
  },
});

export const {
  setTriggerTimeCheck,
  setPopup,
  clearPopups,
  setIlModalData,
  setUnitConverterModalData,
  setURNHistoryModalData,
  setTimeInputPopup,
  setBodyPartModalData,
  setBodyPartPreviewModalData,
  setCaseReportModalData,
  setAutofillPopup,
  setDeisolatedTimeModalOpen,
  setGuernseyType,
  setGuernseyData,
  addGuernseyData,
  removeGuernseyRow,
  setNMEAModalData,
  setNMEAAmendModalData,
  clearNMEAAmendModalData,
  addNewEntryRequest,
  setNewEntryRequest,
  setEngineSelectData,
  clearEngineSelectData,
  // setWasSaved,
  // setWasNMEAModalClosed,
} = generatorFeaturesSlice.actions;

export default generatorFeaturesSlice.reducer;
